// Pure DOM-walking helpers that map a text-selection start node to a share-link comment anchor.
// Kept free of React/MUI so the resolution logic is unit-testable against plain DOM elements.
//
// Markers relied on (already present in the schedule view — see taskRow.js / scheduleIntroduction.js):
//   - `[data-cy^="task-row-"]`            wraps each task; the suffix is the task id
//   - `[data-cy="schedule-intro-content"]` / `[data-cy="schedule-intro-note"]` wrap the introduction body
//   - `[data-comment-part="<part>"]`       wraps each commentable task item (title, frequency, …)

import { getCommentPartFromNode } from "routes/schedule/plugins/share-link-comments/share-link-comment-parts"

const TASK_ROW_PREFIX = "task-row-"
const INTRO_MARKERS = ["schedule-intro-content", "schedule-intro-note"]

/**
 * Resolve the schedule content element that a node lives inside, used to decide whether a selection
 * belongs to the schedule at all. Falls back to `null` when the node is outside the container.
 * @param {Node|null} node
 * @param {Element|null} container
 * @returns {boolean}
 */
export function isNodeInsideContainer(node, container) {
    if (!node || !container) return false
    const element = toElement(node)
    if (!element) return false
    return container === element || container.contains(element)
}

/**
 * Given a selection start node, walk up the DOM to find the enclosing task id (if any).
 * @param {Node|null} node
 * @returns {string|null} the task id encoded in the nearest `data-cy="task-row-<id>"`, or null
 */
export function findTaskIdForNode(node) {
    const taskRow = closestMatching(node, isTaskRowElement)
    if (!taskRow) return null
    return extractTaskId(taskRow)
}

/**
 * Given a selection start node, report whether it lives inside the schedule introduction body.
 * @param {Node|null} node
 * @returns {boolean}
 */
export function isNodeInIntro(node) {
    return !!closestMatching(node, isIntroElement)
}

/**
 * Resolve a selection start node to a comment anchor descriptor without building the full target.
 * The caller turns this descriptor into a real comment target via the presenters (it needs access
 * to the bound schedule's task objects, which this pure helper deliberately does not import).
 *
 * Resolution order: task → intro → schedule (fallback) → none.
 *
 * Task anchors additionally carry the task `part` (title, frequency, …) when the selection sits
 * entirely inside one marked part element — a selection starting in the title but running into the
 * content would otherwise claim "Title" misleadingly, so the part is only set when the start and
 * end of the selection resolve to the same part.
 *
 * @param {object} params
 * @param {Node|null} params.node            selection start node
 * @param {Node|null} [params.endNode]        selection end node (defaults to the start node)
 * @param {Element|null} params.container     schedule content container the selection must be within
 * @param {boolean} [params.allowSchedule]    whether to fall back to a schedule-level anchor
 * @returns {{ type: "task", taskId: string, part: string|null } | { type: "intro" } | { type: "schedule" } | null}
 */
export function resolveSelectionAnchor({ node, endNode, container, allowSchedule = false }) {
    if (container && !isNodeInsideContainer(node, container)) return null
    const taskId = findTaskIdForNode(node)
    if (taskId) return { type: "task", taskId, part: resolveSelectionPart(node, endNode ?? node) }
    if (isNodeInIntro(node)) return { type: "intro" }
    if (allowSchedule) return { type: "schedule" }
    return null
}

function resolveSelectionPart(startNode, endNode) {
    const startPart = getCommentPartFromNode(startNode)
    if (!startPart) return null
    if (getCommentPartFromNode(endNode) !== startPart) return null
    return startPart
}

/**
 * Find the task object in a schedule whose id matches the resolved task id.
 * @param {{ tasks?: Array<{ id?: string }> }|null} schedule
 * @param {string|null} taskId
 * @returns {object|null}
 */
export function findTaskById(schedule, taskId) {
    if (!schedule || !taskId) return null
    const tasks = schedule.tasks ?? []
    return tasks.find((task) => matchesTaskId(task, taskId)) ?? null
}

function matchesTaskId(task, taskId) {
    return task?.id === taskId || task?.taskId === taskId
}

function closestMatching(node, predicate) {
    let element = toElement(node)
    while (element) {
        if (predicate(element)) return element
        element = element.parentElement
    }
    return null
}

function isTaskRowElement(element) {
    const dataCy = getDataCy(element)
    return !!dataCy && dataCy.startsWith(TASK_ROW_PREFIX)
}

function isIntroElement(element) {
    const dataCy = getDataCy(element)
    return !!dataCy && INTRO_MARKERS.includes(dataCy)
}

function extractTaskId(element) {
    const dataCy = getDataCy(element)
    if (!dataCy) return null
    const id = dataCy.slice(TASK_ROW_PREFIX.length)
    return id || null
}

function getDataCy(element) {
    if (!element || typeof element.getAttribute !== "function") return null
    return element.getAttribute("data-cy")
}

function toElement(node) {
    if (!node) return null
    // Text nodes (nodeType 3) carry no attributes; climb to their element parent.
    if (node.nodeType === 1) return node
    return node.parentElement ?? null
}
